import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";

export enum MegaPromptName {
    CultureSentimentAnalysis = "CultureSentimentAnalysisPrompt",
    TeamCVAnalysis = "TeamCVAnalysisPrompt",
    QuarterlyOffsiteReview = "QuarterlyOffsiteReviewPrompt",
    CareerValuationTrend = "CareerValuationTrendPrompt",
    AnalyseEmployeesAtRisk = "AnalyseEmployeesAtRiskPrompt",
    CareerValuationOverdue = "CareerValuationOverduePrompt",
    SummariseMyDashboard = "SummariseMyDashboardPrompt",
    MyOutstandingItems = "MyOutstandingItemsPrompt",
    CulturalIndexOverdue = "CulturalIndexOverduePrompt",
    CulturalIndexTrend = "CulturalIndexTrendPrompt",
    PlayRBABingo = "PlayRBABingoPrompt",
    RBATrend = "RBATrendPrompt",
    TeamAudit = "TeamAuditPrompt",
    MyPeerCatchupAnalysis = "MyPeerCatchupAnalysisPrompt",
    MyCareerValuationTrend = "MyCareerValuationTrendPrompt",
    MyCulturalIndexTrend = "MyCulturalIndexTrendPrompt",
    BoardAnalysisPrompt = "BoardAnalysisPrompt",
    TeamAssessmentTrend = "TeamAssessmentTrendPrompt",
    AnalyseSurveyResult = "AnalyseSurveyResultPrompt",
    AnalyseEmployeeEngagementTrend = "AnalyseEmployeeEngagementTrendPrompt",

    // nimbus mega prompts
    AtRiskAccountsAnalysis = "AtRiskAccountsAnalysisPrompt",
    BillingTierAudit = "BillingTierAuditPrompt",
}

export interface IMegaPromptPermission {
    permissionName: FeaturePermissionName;
    globalOnly?: boolean;
}

export interface IMegaPromptMetadata {
    label: string;
    permissions: IMegaPromptPermission[];
}

export interface IMegaPromptEntry {
    name: MegaPromptName;
    // For now the only argument that's needed is team entity for mega prompt that will only show up in leadership team meeting.
    //  e.g. Quarterly Offsite Review.
    // So I can make it a sync call for now to simplify the flow - if there are other entities required for the future,
    // may make this an async call and pass in injector instead to resolve necessary data for the condition function.
    condition?: (team?: Team) => boolean;
}

export const MegaPromptMetadata: Record<MegaPromptName, IMegaPromptMetadata> = {
    [MegaPromptName.CultureSentimentAnalysis]: {
        label: "Culture Sentiment Analysis",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCareerValuationRead, globalOnly: true },
            { permissionName: FeaturePermissionName.CulturalNetworkCulturalIndexRead, globalOnly: true },
            { permissionName: FeaturePermissionName.PeoplePeerCatchUpRead, globalOnly: true },
        ],
    },
    [MegaPromptName.TeamCVAnalysis]: {
        label: "Team CV Analysis",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCareerValuationRead, globalOnly: true },
        ],
    },
    [MegaPromptName.QuarterlyOffsiteReview]: {
        label: "Quarterly Offsite Review",
        permissions: [
            { permissionName: FeaturePermissionName.StewardshipWorkMeetingsEdit },
        ],
    },
    [MegaPromptName.CareerValuationTrend]: {
        label: "Career Valuation Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCareerValuationRead, globalOnly: true },
        ],
    },
    [MegaPromptName.AnalyseEmployeesAtRisk]: {
        label: "Analyse Employees at Risk",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCareerValuationRead, globalOnly: true },
        ],
    },
    [MegaPromptName.CareerValuationOverdue]: {
        label: "Career Valuation Overdue",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCareerValuationRead, globalOnly: true },
        ],
    },
    [MegaPromptName.SummariseMyDashboard]: {
        label: "Summarise My Dashboard",
        permissions: [],
    },
    [MegaPromptName.MyOutstandingItems]: {
        label: "My Outstanding Items",
        permissions: [],
    },
    [MegaPromptName.CulturalIndexOverdue]: {
        label: "Cultural Index Overdue",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCulturalIndexRead, globalOnly: true },
        ],
    },
    [MegaPromptName.CulturalIndexTrend]: {
        label: "Cultural Index Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCulturalIndexRead, globalOnly: true },
        ],
    },
    [MegaPromptName.PlayRBABingo]: {
        label: "Play RBA Bingo",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkResilientBusinessAssessmentRead },
        ],
    },
    [MegaPromptName.RBATrend]: {
        label: "RBA Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkResilientBusinessAssessmentRead },
        ],
    },
    [MegaPromptName.TeamAudit]: {
        label: "Team Audit",
        permissions: [
            { permissionName: FeaturePermissionName.CompassGuidingPhilosophyEdit },
        ],
    },
    [MegaPromptName.MyPeerCatchupAnalysis]: {
        label: "My Peer Catchup Analysis",
        permissions: [
            { permissionName: FeaturePermissionName.PeoplePeerCatchUpPersonalUseAndRead },
        ],
    },
    [MegaPromptName.MyCareerValuationTrend]: {
        label: "My Career Valuation Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCareerValuationPersonalUseAndRead },
        ],
    },
    [MegaPromptName.MyCulturalIndexTrend]: {
        label: "My Cultural Index Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkCulturalIndexPersonalUseAndRead },
        ],
    },
    [MegaPromptName.BoardAnalysisPrompt]: {
        label: "Board Analysis Prompt",
        permissions: [
            { permissionName: FeaturePermissionName.StewardshipWorkKanbanRead },
            { permissionName: FeaturePermissionName.StewardshipWorkKanbanPublicRead, globalOnly: true },
            { permissionName: FeaturePermissionName.StewardshipWorkKanbanPersonalEdit, globalOnly: true },
        ],
    },
    [MegaPromptName.TeamAssessmentTrend]: {
        label: "Team Assessment Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkTeamAssessmentRead },
        ],
    },
    [MegaPromptName.AnalyseSurveyResult]: {
        label: "Analyse Survey Result",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkTeamAssessmentRead },
            { permissionName: FeaturePermissionName.CulturalNetworkEmployeeEngagementRead },
        ],
    },
    [MegaPromptName.AnalyseEmployeeEngagementTrend]: {
        label: "Analyse Employee Engagement Trend",
        permissions: [
            { permissionName: FeaturePermissionName.CulturalNetworkEmployeeEngagementRead },
        ],
    },

    // nimbus mega prompts won't need permission check
    [MegaPromptName.AtRiskAccountsAnalysis]: {
        label: "At Risk Accounts Analysis",
        permissions: [],
    },
    [MegaPromptName.BillingTierAudit]: {
        label: "Billing Tier Audit",
        permissions: [],
    },
};
